import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  OnInit,
  TemplateRef,
  Type
} from '@angular/core';
import {  ModalRef } from '@celerofinancas/ui-modals';

/**
 * Modal data contract used by the base component.
 */
interface ModalBaseData {
  headerTitle?: string;
  showCloseModal?: boolean;
  showHeader?: boolean;
}

/**
* ModalBaseComponent serves as a base component for modals in the application.
*/
@Component({
  selector: 'caixa-webcomponent-modal-base',
  templateUrl: './modal-base.component.html',
  standalone: true,
  imports: [
    CommonModule,
  ],
  styleUrls: ['./modal-base.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModalBaseComponent implements OnInit, AfterViewInit {
  /**
   * Change detector ref for manual change detection
   */
  private cdr = inject(ChangeDetectorRef);

  /**
   * Content type of modal
   */
  public contentType: 'template' | 'string' | 'component';

  /**
   * Content of modal
   */
  public content: string | TemplateRef<any> | Type<any>;

  /**
   * Define a generic context for the template type modal
   */
  public context: { data: any; close: (data?: any) => void };

  /**
   * Modal Reference
   */
  public readonly modalRef = inject(ModalRef);

  /**
   * Header text for the modal.
   */
  public headerTitle: string;

  /**
   * Flag to show/hide the close modal button.
   */
  public showCloseModal = true;

  /**
   * Flag to show/hide the modal header.
   */
  public showHeader = true;

  /**
   * Get the content as a string if it is of type string
   */
  public get templateContent(): TemplateRef<any> | null {
    return this.contentType === 'template' && this.content instanceof TemplateRef
      ? this.content
      : null;
  }

  /**
   * Get the content as a string if it is of type string
   */
  public get componentContent(): Type<any> | null {
    return this.contentType === 'component' && typeof this.content !== 'string'
      ? this.content as Type<any>
      : null;
  }

  /**
   * Close modal
   */
  public close(): void {
    this.modalRef.close();
  }


   /**
   * Initialize
   */
  public ngOnInit(): void {
    this.modalRef.afterOpened$.next();
    this.modalRef.afterOpened$.complete();
    this.content = this.modalRef.content;
    this.updateModalData();

    if (typeof this.content === 'string') {
      this.contentType = 'string';
    } else if (this.content instanceof TemplateRef) {
      this.contentType = 'template';
      this.context = {
        data: this.modalRef.data,
        close: this.modalRef.close.bind(this.modalRef),
      };
    } else {
      this.contentType = 'component';
    }
  }

  /**
   * Lifecycle hook that is called after the component's view has been fully initialized.
   */
  public ngAfterViewInit(): void {
    this.updateModalData();
  }

  /**
   * Updates modal data such as header title and visibility of close button based on the data provided in the modal reference.
   */
  public updateModalData(): void {
    const modalData = this.modalRef.data as ModalBaseData | undefined;

    if (!modalData) {
      return;
    }

    this.showCloseModal = modalData.showCloseModal ?? true;
    this.showHeader = modalData.showHeader ?? true;

    if (modalData.headerTitle) {
      this.headerTitle = modalData.headerTitle;
    }

    this.cdr.detectChanges();
  }
}
